import React, { useState, useEffect, useRef } from 'react';

import { api } from '../../../services/api';
import { session } from '../../../services/session';
import { Loader } from '../../misc/loader-utils';

import ProductModal from './product-modal';

import './strategy-typeahead.scss';

const optionHeight = 50;
const maxHeight = 390;

function ProductTypeahead({ type, triggerProp, updateChips }) {
  /* State */
  const [textInput, setInput] = useState('');
  const [invalidCount, setInvalidCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputNode = useRef(null);

  /* Constants */
  const endpoint = `/api/v0/merchants/${session.orgId}/strategy/validate_products/`;
  const pluralType = 'skus';
  const triggers = triggerProp || [];

  /* Effects */
  useEffect(() => {
    const onWindowClick = e => {
      if (!inputNode.current) return;

      if (!inputNode.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    window.addEventListener('click', onWindowClick);
    return () => {
      window.removeEventListener('click', onWindowClick);
    };
  }, []);

  /* Methods */
  function queryValidation() {
    setLoading(true);
    const skus = textInput
      .trim()
      .split(',')
      .filter(x => x)
      .join(',');

    api.get(endpoint, { skus }).then(resp => {
      const { stats } = resp.data.data[0];
      const validProducts = stats.valid_skus;
      const invalidSkus = stats.invalid_skus;

      updateChips(pluralType, triggers.concat(validProducts), null, true);
      setInvalidCount(invalidSkus.length);
      setInput('');

      setLoading(false);
    });
  }

  function confirmTriggers(stats, totalErrors) {
    setInvalidCount(invalidCount + totalErrors);
    updateChips(pluralType, triggers.concat(stats), null, true);
  }

  function selectAll() {
    updateChips(pluralType, [], null, true);
  }

  function selectOption(selected) {
    const newTriggers = triggers.filter(item => item.sku !== selected.sku);
    updateChips(pluralType, newTriggers, null, true);
  }

  function handleKeyInputs(e) {
    if (e.key === 'Enter') {
      queryValidation();
    }
  }

  /* Render */
  const largeClass = textInput.length > 95 ? 'large' : '';

  const chips =
    !loading && (triggers.length || invalidCount) ? (
      <>
        <div className={`chip-container ${type}`}>
          <div className="chip-label" onClick={() => setShowDropdown(!showDropdown)}>
            SKU IDs ({triggers.length})
          </div>
          <div
            className="delete-chip"
            onClick={() => {
              updateChips(pluralType, [], null, true);
              setInvalidCount(0);
            }}
          />
        </div>
        <div className={`chip-container excluded ${type}`}>
          <div className="chip-label">Errors removed ({invalidCount})</div>
        </div>
      </>
    ) : null;

  const dropdownOptions = triggers.map((option, i) => {
    return (
      <div key={`option-${i}`} className="option selected" onClick={() => selectOption(option)}>
        <span className="checkbox"></span>
        <span className="value">{option.name}</span>
        <span className="sku">{option.sku}</span>
      </div>
    );
  });

  const rowHeight = (triggers.length + 1) * optionHeight;
  const containerHeight = rowHeight > maxHeight ? maxHeight + 10 : rowHeight + 10;

  const dropdown =
    triggers.length && showDropdown ? (
      <div className="typeahead-container" style={{ '--search-height': `-${containerHeight}px` }}>
        <div className="select-all" onClick={selectAll}>
          Deselect All ({triggers.length})
        </div>
        {dropdownOptions}
      </div>
    ) : null;

  const modal = showModal ? (
    <ProductModal
      triggers={triggers}
      setShowModal={setShowModal}
      confirmTriggers={confirmTriggers}
    />
  ) : null;

  const inputField =
    !triggers.length && !invalidCount && !loading ? (
      <textarea
        className={`input ${largeClass}`}
        value={textInput}
        placeholder="Add product SKU ID"
        spellCheck={false}
        onChange={e => (!loading ? setInput(e.target.value) : null)}
        onKeyDown={handleKeyInputs}
      />
    ) : null;

  const modalButton = (
    <div className="upload" onClick={() => setShowModal(true)}>
      or <span>Upload a CSV</span>
    </div>
  );

  return (
    <div className={`${type}-typeahead modal-enabled`}>
      <div className={`label ${largeClass}`}>{type}</div>
      <div ref={node => (inputNode.current = node)} className="trigger-input">
        {chips}
        {inputField}
        {dropdown}
        <Loader loading={loading} />
        {modalButton}
      </div>
      <div className={`instruction ${largeClass}`}>
        Paste in a list of Product SKU IDs separated by commas. Max 200.
      </div>
      {modal}
    </div>
  );
}

export default ProductTypeahead;
